import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  TrialBalanceFilter, TrialBalanceDto,
  AccountLedgerFilter, AccountLedgerDto,
  GlInquiryFilter, GlInquiryRow,
  GeneralLedgerFilter, GeneralLedgerDto,
  FinancialStatementFilter, FinancialStatementDto,
  CashBankBookFilter,
  CashBankBookDto,
  DayBookFilter,
  DayBookDto,
} from '@/types/report.types';

export const reportApi = {
  /** GET /api/v1/reports/trial-balance */
  getTrialBalance: (filter: TrialBalanceFilter) =>
    apiClient.get<ApiResponse<TrialBalanceDto>>('/reports/trial-balance', { params: filter }),

  /** GET /api/v1/reports/account-ledger */
  getAccountLedger: (filter: AccountLedgerFilter) =>
    apiClient.get<ApiResponse<AccountLedgerDto>>('/reports/account-ledger', { params: filter }),

  /** GET /api/v1/reports/cash-bank-book — Cash Book (bookType CASH) or Bank Book (BANK) */
  getCashBankBook: (filter: CashBankBookFilter) =>
    apiClient.get<ApiResponse<CashBankBookDto>>('/reports/cash-bank-book', { params: filter }),

  /** GET /api/v1/reports/day-book */
  getDayBook: (filter: DayBookFilter) =>
    apiClient.get<ApiResponse<DayBookDto>>('/reports/day-book', { params: filter }),

  /** GET /api/v1/reports/gl-inquiry */
  getGlInquiry: (filter: GlInquiryFilter) =>
    apiClient.get<ApiResponse<PagedResult<GlInquiryRow>>>('/reports/gl-inquiry', { params: filter }),

  /**
   * GET /api/v1/reports/gl-inquiry/export-csv — full filtered extraction as CSV.
   * Requires EXPORT_RPT_DATA. Fetched as a blob (the JWT interceptor cannot
   * decorate a plain anchor navigation) and saved via URL.createObjectURL.
   */
  exportGlInquiryCsv: (filter: GlInquiryFilter) =>
    apiClient.get<Blob>('/reports/gl-inquiry/export-csv', {
      params: filter,
      responseType: 'blob',
      timeout: 300000, // large extractions stream in pages server-side
    }),

  /** GET /api/v1/reports/general-ledger — formal multi-account ledger (paged by account) */
  getGeneralLedger: (filter: GeneralLedgerFilter) =>
    apiClient.get<ApiResponse<GeneralLedgerDto>>('/reports/general-ledger', { params: filter }),

  /** GET /api/v1/reports/profit-and-loss */
  getProfitAndLoss: (filter: FinancialStatementFilter) =>
    apiClient.get<ApiResponse<FinancialStatementDto>>('/reports/profit-and-loss', { params: filter }),

  /** GET /api/v1/reports/balance-sheet */
  getBalanceSheet: (filter: FinancialStatementFilter) =>
    apiClient.get<ApiResponse<FinancialStatementDto>>('/reports/balance-sheet', { params: filter }),
};
